import React, { useEffect, useState } from 'react';
import { Segment, Table } from 'semantic-ui-react';
import styled from 'styled-components';

import Button from '../../../components/Button';
import TablePaginationMenu from '../../../components/TablePaginationMenu';
import {
  activateStandaloneChargeCommissionCollaborator,
  createStandaloneChargeCommissionCollaborator,
  deleteStandaloneChargeCommissionCollaborator,
  getStandaloneChargeCommissionCollaborator,
  listStandaloneChargeCommissionCollaborators,
  StandaloneChargeCommissionCollaboratorDTO,
  updateStandaloneChargeCommissionCollaborator
} from '../../../services/standalone-charges';
import {
  maskCpfOrCnpj,
  maskPhoneNumber,
  normalizeDocument,
  unmaskPhoneNumber
} from '../../../services/masks';
import { dangerNotification, successNotification } from '../../../services/notification';
import { updateUser } from '../../../services/user';
import { onlyDigits } from '../../../shared/documents';

import { initialCommissioningFilters } from '../constants';
import {
  CommissioningCollaborator,
  CommissioningCollaboratorStatus,
  CreateCommissioningCollaboratorFormState
} from '../types';
import { buildPagarmeActivationPayload } from '../utils/buildPagarmeActivationPayload';
import { clearActivationDraft, hasActivationDraft } from '../utils/activationDraftStorage';
import CommissioningActionsMenu from './CommissioningActionsMenu';
import CommissioningFiltersBar from './CommissioningFiltersBar';
import CreateCollaboratorModal from './CreateCollaboratorModal';
import DeleteCollaboratorModal from './DeleteCollaboratorModal';
import EditCollaboratorModal from './EditCollaboratorModal';
import EditBankAccountModal from './EditBankAccountModal';
import RegisterBiometryModal from './RegisterBiometryModal';
import CollaboratorDetailsModal from './CollaboratorDetailsModal';

const statusLabelMap: Record<CommissioningCollaboratorStatus, string> = {
  ACTIVE: 'Ativo',
  INACTIVE: 'Inativo',
  AWAITING_BIOMETRY: 'Aguardando biometria',
  BIOMETRY_REJECTED: 'Biometria recusada',
  SUPPORT_REQUIRED: 'Análise necessária'
};

const errorNotificationDurationMs = 9000;
const successNotificationDurationMs = 5000;

const TableWrapper = styled.div`
  overflow: visible;

  @media (max-width: 991px) {
    overflow-x: auto;
    padding-bottom: 120px;
  }
`;

const formatCreatedAt = (createdAt?: string) => {
  if (!createdAt) return '-';

  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) return '-';

  return date.toLocaleString('pt-BR');
};

const stopRowOpen = (e: React.SyntheticEvent) => {
  e.stopPropagation();
};

const mapCollaborator = (
  collaborator: StandaloneChargeCommissionCollaboratorDTO
): CommissioningCollaborator => {
  const documentValue = collaborator.user?.doc
    ? maskCpfOrCnpj(collaborator.user.doc)
    : '-';

  return {
    id: collaborator.id,
    estateId: collaborator.estateId,
    estateName: collaborator.estate?.name || '-',
    userId: collaborator.user?.id || '',
    createdAt: collaborator.created_at || '',
    name: collaborator.user?.name || '-',
    email: collaborator.user?.email || '',
    document: documentValue,
    cpf: documentValue,
    recipientType: collaborator.activation?.recipientType,
    phone: collaborator.user?.cellphone
      ? maskPhoneNumber(collaborator.user.cellphone)
      : '-',
    responsible: collaborator.responsable?.name || '-',
    status: collaborator.status,
    activation: collaborator.activation
  };
};

const formatApiMessage = (message: unknown): string => {
  if (Array.isArray(message)) {
    return message
      .map(item => formatApiMessage(item))
      .filter(Boolean)
      .join('\n');
  }

  if (typeof message === 'string') {
    const trimmedMessage = message.trim();
    if (!trimmedMessage) return '';

    try {
      const parsedMessage = JSON.parse(trimmedMessage);
      if (parsedMessage !== trimmedMessage) {
        return formatApiMessage(parsedMessage);
      }
    } catch (error) {
      // Mensagem comum, sem JSON.
    }

    return trimmedMessage;
  }

  if (typeof message === 'object' && message !== null && 'message' in message) {
    return formatApiMessage((message as { message?: unknown }).message);
  }

  return '';
};

const getApiErrorMessage = (error: unknown, fallback: string): string => {
  if (typeof error === 'object' && error !== null) {
    const errorObject = error as {
      message?: unknown;
      data?: unknown;
      response?: { data?: unknown };
    };

    return (
      formatApiMessage(errorObject.message) ||
      formatApiMessage(errorObject.data) ||
      formatApiMessage(errorObject.response?.data) ||
      fallback
    );
  }

  return formatApiMessage(error) || fallback;
};

const syncLinkedUserForPfCollaborator = async (
  form: CreateCommissioningCollaboratorFormState
) => {
  if (!form.userId || form.recipientType !== 'individual') {
    return;
  }

  await updateUser(form.userId, {
    id: form.userId,
    doc: onlyDigits(form.document),
    cellphone: unmaskPhoneNumber(form.phone)
  });
};

type Props = {
  isBrokerAdmin: boolean;
  isEstateAdmin: boolean;
  defaultEstateId: string;
  estateOptions: Array<{ key: string; text: string; value: string }>;
  currentUserId?: string;
  currentUserDocument?: string;
};

const CommissioningChargesList: React.FC<Props> = ({
  isBrokerAdmin,
  isEstateAdmin,
  defaultEstateId,
  estateOptions,
  currentUserId,
  currentUserDocument
}) => {
  const [filters, setFilters] = useState(initialCommissioningFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialCommissioningFilters);
  const [collaborators, setCollaborators] = useState<CommissioningCollaborator[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [savingCreate, setSavingCreate] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [biometryModalOpen, setBiometryModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [detailsCollaboratorId, setDetailsCollaboratorId] = useState<string | null>(null);
  const [editBankAccountOpen, setEditBankAccountOpen] = useState(false);
  const [editBankAccountSource, setEditBankAccountSource] = useState<CommissioningCollaborator | null>(
    null
  );
  const [selectedCollaborator, setSelectedCollaborator] =
    useState<CommissioningCollaborator | null>(null);

  const loadCollaborators = async (page = currentPage, pageFilters = appliedFilters) => {
    setIsLoading(true);
    try {
      const nameFilter = pageFilters.type === 'name' ? pageFilters.argument : '';
      const documentFilter =
        pageFilters.type === 'document'
          ? normalizeDocument(pageFilters.argument) || onlyDigits(pageFilters.argument)
          : '';

      const response = await listStandaloneChargeCommissionCollaborators({
        page,
        limit: 10,
        estateId: pageFilters.estateId || undefined,
        name: nameFilter || undefined,
        document: documentFilter || undefined,
        status: pageFilters.status || undefined
      });

      setCollaborators((response.data || []).map(mapCollaborator));
      setTotal(response.count || 0);
    } catch (error) {
      dangerNotification('Erro', 'Não foi possível carregar colaboradores.');
    } finally {
      setIsLoading(false);
    }
  };

  const canManageCollaborators = isBrokerAdmin || isEstateAdmin;
  const showEstateColumn = isBrokerAdmin;
  const emptyStateColSpan = showEstateColumn ? 8 : 7;
  const currentUserDocumentValue = currentUserDocument
    ? onlyDigits(currentUserDocument)
    : '';

  useEffect(() => {
    loadCollaborators(currentPage, appliedFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, appliedFilters]);

  const handleSearch = () => {
    setCurrentPage(1);
    setAppliedFilters(filters);
  };

  const clearFilters = () => {
    setFilters(initialCommissioningFilters);
    setCurrentPage(1);
    setAppliedFilters(initialCommissioningFilters);
  };

  const createCollaborator = async (form: CreateCommissioningCollaboratorFormState) => {
    setSavingCreate(true);
    try {
      await syncLinkedUserForPfCollaborator(form);

      await createStandaloneChargeCommissionCollaborator({
        estateId: form.estateId || undefined,
        userId: form.userId || undefined,
        activation: buildPagarmeActivationPayload(form)
      });
      if (form.estateId) {
        clearActivationDraft({ type: 'collaborator-create', estateId: form.estateId });
      }
      clearActivationDraft({ type: 'collaborator-create-latest' });
      setCreateModalOpen(false);
      successNotification(
        'Sucesso',
        'Colaborador cadastrado com sucesso.',
        'bottom',
        successNotificationDurationMs
      );
      setCurrentPage(1);
      await loadCollaborators(1, appliedFilters);
    } catch (error) {
      dangerNotification(
        'Erro',
        getApiErrorMessage(error, 'Não foi possível adicionar colaborador.'),
        errorNotificationDurationMs
      );
    } finally {
      setSavingCreate(false);
    }
  };

  const openEditModal = (collaborator: CommissioningCollaborator) => {
    if (collaborator.status === 'ACTIVE') {
      openEditBankAccount(collaborator);
      return;
    }
    setSelectedCollaborator(collaborator);
    setEditModalOpen(true);
  };

  const openEditBankAccount = async (collaborator: CommissioningCollaborator) => {
    try {
      const detail = await getStandaloneChargeCommissionCollaborator(collaborator.id);
      setEditBankAccountSource({
        ...collaborator,
        recipientType: detail.activation?.recipientType || collaborator.recipientType,
        activation: detail.activation
          ? {
              recipientType: detail.activation.recipientType,
              registerInformation: detail.activation.registerInformation,
              defaultBankAccount: detail.activation.defaultBankAccount,
              payload: detail.activation.payload
            }
          : collaborator.activation
      });
      setEditBankAccountOpen(true);
    } catch {
      dangerNotification('Erro', 'Não foi possível carregar os dados bancários do colaborador.');
    }
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setSelectedCollaborator(null);
  };

  const updateCollaborator = async (form: CreateCommissioningCollaboratorFormState) => {
    if (!selectedCollaborator) return;

    setSavingEdit(true);
    try {
      if (form.userId !== selectedCollaborator.userId) {
        await updateStandaloneChargeCommissionCollaborator(selectedCollaborator.id, {
          userId: form.userId || null
        });
      }

      await syncLinkedUserForPfCollaborator(form);

      await activateStandaloneChargeCommissionCollaborator(
        selectedCollaborator.id,
        buildPagarmeActivationPayload(form)
      );

      clearActivationDraft({
        type: 'collaborator-edit',
        collaboratorId: selectedCollaborator.id
      });

      closeEditModal();
      successNotification(
        'Sucesso',
        'Colaborador editado com sucesso.',
        'bottom',
        successNotificationDurationMs
      );
      await loadCollaborators(currentPage, appliedFilters);
    } catch (error) {
      dangerNotification(
        'Erro',
        getApiErrorMessage(error, 'Não foi possível editar colaborador.'),
        errorNotificationDurationMs
      );
    } finally {
      setSavingEdit(false);
    }
  };

  const openBiometryModal = (collaborator: CommissioningCollaborator) => {
    setSelectedCollaborator(collaborator);
    setBiometryModalOpen(true);
  };

  const closeBiometryModal = () => {
    setBiometryModalOpen(false);
    setSelectedCollaborator(null);
  };

  const editRegistrationFromBiometry = (collaborator: CommissioningCollaborator) => {
    setBiometryModalOpen(false);
    openEditModal(collaborator);
  };

  const openDeleteModal = (collaborator: CommissioningCollaborator) => {
    setSelectedCollaborator(collaborator);
    setDeleteModalOpen(true);
  };

  const openCollaboratorDetails = (collaboratorId: string) => {
    setDetailsCollaboratorId(collaboratorId);
    setDetailsModalOpen(true);
  };

  const closeCollaboratorDetails = () => {
    setDetailsModalOpen(false);
    setDetailsCollaboratorId(null);
  };

  const openBiometryFromDetails = (collaboratorId: string) => {
    const collaborator = collaborators.find(item => item.id === collaboratorId) || null;
    closeCollaboratorDetails();
    if (collaborator) {
      openBiometryModal(collaborator);
    }
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setSelectedCollaborator(null);
  };

  const confirmDeleteCollaborator = async () => {
    if (!selectedCollaborator) return;

    setDeleting(true);
    try {
      await deleteStandaloneChargeCommissionCollaborator(selectedCollaborator.id);
      closeDeleteModal();
      successNotification(
        'Sucesso',
        'Colaborador removido com sucesso.',
        'bottom',
        successNotificationDurationMs
      );
      await loadCollaborators(currentPage, appliedFilters);
    } catch (error) {
      dangerNotification(
        'Erro',
        getApiErrorMessage(error, 'Não foi possível remover colaborador.'),
        errorNotificationDurationMs
      );
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <CommissioningFiltersBar
        isBrokerAdmin={isBrokerAdmin}
        estateOptions={estateOptions}
        filters={filters}
        setFilters={setFilters}
        onSearch={handleSearch}
        onClear={clearFilters}
      />

      <Segment loading={isLoading}>
        <div
          style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}
        >
          <h3 style={{ margin: 0 }}>Colaboradores</h3>
          {(isBrokerAdmin || isEstateAdmin) && (
            <Button
              type="button"
              text={
                (
                  defaultEstateId &&
                  hasActivationDraft({
                    type: 'collaborator-create',
                    estateId: defaultEstateId
                  })
                ) ||
                hasActivationDraft({ type: 'collaborator-create-latest' })
                  ? 'Continuar cadastro'
                  : 'Adicionar colaborador'
              }
              onClick={() => setCreateModalOpen(true)}
            />
          )}
        </div>

        <TableWrapper>
          <Table celled striped unstackable selectable style={{ minWidth: 980 }}>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Criado Em</Table.HeaderCell>
                {showEstateColumn && <Table.HeaderCell>Imobiliária</Table.HeaderCell>}
                <Table.HeaderCell>Nome</Table.HeaderCell>
                <Table.HeaderCell>Documento</Table.HeaderCell>
                <Table.HeaderCell>Telefone/Celular</Table.HeaderCell>
                <Table.HeaderCell>Responsável</Table.HeaderCell>
                <Table.HeaderCell>Status</Table.HeaderCell>
                <Table.HeaderCell>Ações</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {collaborators.length === 0 && !isLoading ? (
                <Table.Row>
                  <Table.Cell colSpan={emptyStateColSpan} textAlign="center">
                    Nenhum colaborador encontrado.
                  </Table.Cell>
                </Table.Row>
              ) : (
                collaborators.map(collaborator => (
                  <Table.Row
                    key={collaborator.id}
                    style={{ cursor: 'pointer' }}
                    tabIndex={0}
                    onClick={() => openCollaboratorDetails(collaborator.id)}
                    onKeyDown={(e: React.KeyboardEvent<HTMLTableRowElement>) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openCollaboratorDetails(collaborator.id);
                      }
                    }}
                    aria-label={`Abrir detalhes do colaborador ${collaborator.name}`}
                  >
                    <Table.Cell>{formatCreatedAt(collaborator.createdAt)}</Table.Cell>
                    {showEstateColumn && (
                      <Table.Cell>{collaborator.estateName}</Table.Cell>
                    )}
                    <Table.Cell>{collaborator.name}</Table.Cell>
                    <Table.Cell>{collaborator.document}</Table.Cell>
                    <Table.Cell>{collaborator.phone}</Table.Cell>
                    <Table.Cell>{collaborator.responsible}</Table.Cell>
                    <Table.Cell>{statusLabelMap[collaborator.status]}</Table.Cell>
                    <Table.Cell textAlign="center" onClick={stopRowOpen}>
                      <CommissioningActionsMenu
                        collaborator={collaborator}
                        canManage={canManageCollaborators}
                        isOwnCollaborator={Boolean(
                          (currentUserId && collaborator.userId === currentUserId) ||
                            (collaborator.recipientType !== 'corporation' &&
                              currentUserDocumentValue &&
                              onlyDigits(collaborator.document) ===
                                currentUserDocumentValue)
                        )}
                        onEdit={openEditModal}
                        onEditBankAccount={openEditBankAccount}
                        onRegisterBiometry={openBiometryModal}
                        onDelete={openDeleteModal}
                      />
                    </Table.Cell>
                  </Table.Row>
                ))
              )}
            </Table.Body>
          </Table>
        </TableWrapper>

        {total > 0 && (
          <TablePaginationMenu
            currentPage={currentPage}
            dataCount={total}
            setCurrentPage={setCurrentPage}
          />
        )}
      </Segment>

      <CreateCollaboratorModal
        open={createModalOpen}
        isBrokerAdmin={isBrokerAdmin}
        isEstateAdmin={isEstateAdmin}
        defaultEstateId={defaultEstateId}
        estateOptions={estateOptions}
        saving={savingCreate}
        onClose={() => setCreateModalOpen(false)}
        onSubmit={createCollaborator}
      />

      <EditCollaboratorModal
        open={editModalOpen}
        collaborator={selectedCollaborator}
        isBrokerAdmin={isBrokerAdmin}
        isEstateAdmin={isEstateAdmin}
        estateOptions={estateOptions}
        saving={savingEdit}
        onClose={closeEditModal}
        onSubmit={updateCollaborator}
      />

      <RegisterBiometryModal
        open={biometryModalOpen}
        collaborator={selectedCollaborator}
        onClose={closeBiometryModal}
        onEditRegistration={canManageCollaborators ? editRegistrationFromBiometry : undefined}
        onRetrySuccess={() => loadCollaborators(currentPage, appliedFilters)}
      />

      <DeleteCollaboratorModal
        open={deleteModalOpen}
        collaborator={selectedCollaborator}
        loading={deleting}
        onClose={closeDeleteModal}
        onConfirm={confirmDeleteCollaborator}
      />

      <CollaboratorDetailsModal
        open={detailsModalOpen}
        collaboratorId={detailsCollaboratorId}
        hideEstateSection={!showEstateColumn}
        onClose={closeCollaboratorDetails}
        onRegisterBiometry={openBiometryFromDetails}
        canEditBankAccount={canManageCollaborators}
        onBankAccountUpdated={() => loadCollaborators(currentPage, appliedFilters)}
      />

      <EditBankAccountModal
        open={editBankAccountOpen}
        mode="collaborator"
        collaboratorId={editBankAccountSource?.id}
        source={
          editBankAccountSource
            ? {
                recipientType: editBankAccountSource.recipientType,
                registerInformation: editBankAccountSource.activation?.registerInformation,
                defaultBankAccount: editBankAccountSource.activation?.defaultBankAccount,
                payload: editBankAccountSource.activation?.payload
              }
            : null
        }
        onClose={() => {
          setEditBankAccountOpen(false);
          setEditBankAccountSource(null);
        }}
        onSuccess={async () => {
          await loadCollaborators(currentPage, appliedFilters);
        }}
      />
    </>
  );
};

export default CommissioningChargesList;
